import {
  Input,
  Output,
  OnInit,
  Component,
  EventEmitter,
  AfterViewInit,
  inject,
  DestroyRef,
  ChangeDetectionStrategy,
} from '@angular/core';

import { UserObjectsService } from 'src/app/core/services/user-objects.service';

import type { WidgetMoveAction } from '../move-copy-widget-dialog.component';

import { DataObject } from 'src/app/core/models/core.models';
import { NomObjectClass, NomObjectTypes } from 'src/app/core/constants';
import { OtherDashboardsOperation } from 'src/app/core/models/widget-operations';

import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { PICKER_DATAOBJECTS_DIALOG } from 'src/app/core/services/picker-dataobjects-dialog.token';
import { DialogCloseReason, DialogService } from '@sui/dialog';
import { EMPTY } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgpRovingFocusGroup } from 'ng-primitives/roving-focus';
import { NgpRadioGroup } from 'ng-primitives/radio';
import { SuiRadioGroupDirective, SuiRadioComponent } from '@sui/radio';
import { FormsModule } from '@angular/forms';
import { SuiSelectComponent, SuiSelectOptionComponent } from '@sui/select';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { SuiButtonComponent } from '@sui/button';
import { UpperCasePipe } from '@angular/common';
type PageStrategy = 'first' | 'last' | 'new';

@Component({
  selector: 'app-move-copy-other-dashboards',
  templateUrl: './move-copy-other-dashboards.component.html',
  styleUrl: './move-copy-other-dashboards.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgpRovingFocusGroup,
    NgpRadioGroup,
    SuiRadioGroupDirective,
    FormsModule,
    SuiRadioComponent,
    SuiSelectComponent,
    SuiSelectOptionComponent,
    FaIconComponent,
    SuiButtonComponent,
    UpperCasePipe,
    TranslatePipe,
  ],
})
export class MoveCopyOtherDashboardsComponent implements OnInit, AfterViewInit {
  @Input() widgets: DataObject[] = [];
  @Input() currentObject?: DataObject;
  @Output() formValidityChange = new EventEmitter<boolean>();

  globalAction: WidgetMoveAction = 'copy';
  pageStrategy: PageStrategy = 'first';
  selectedDashboards: DataObject[] = [];
  selectedDashboardIds: string[] = [];

  pageStrategyOptions = [
    { value: 'first', label: 'First Page' },
    { value: 'last', label: 'Last Page' },
    { value: 'new', label: 'New Page' },
  ];

  private translateService = inject(TranslateService);
  private dialogService = inject(DialogService);
  private destroyRef = inject(DestroyRef);
  // Token, not the class: see picker-dataobjects-dialog.token.ts.
  private pickerDialog = inject(PICKER_DATAOBJECTS_DIALOG);
  constructor(private userObjects: UserObjectsService) {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    // * initially invalid until Dashboards are selected
    setTimeout(() => {
      this.emitValidity();
    });
  }

  private emitValidity(): void {
    this.formValidityChange.emit(this.selectedDashboardIds.length > 0);
  }

  onGlobalActionChange(action: WidgetMoveAction): void {
    this.globalAction = action;
  }

  getActionDisplayText(action: WidgetMoveAction): string {
    return this.translateService.instant(
      action === 'copy'
        ? 'move-copy-widget-dialog.label.action-copy'
        : 'move-copy-widget-dialog.label.action-move',
    );
  }

  openDashboardPicker(): void {
    const dialogRef = this.dialogService.openTabsDialog(
      this.pickerDialog,
      {
        data: {
          objectClassIds: [NomObjectClass.DASHBOARD],
          objectTypeIds: [NomObjectTypes.DASHBOARD],
          selectedObjectIds: new Map(
            this.selectedDashboards.map((dashboard) => [
              dashboard.id,
              {
                object_id: dashboard.id,
                object_class_id: dashboard.object_class_id,
              },
            ]),
          ),
        },
        closeOnClick: false,
        closeOnEscape: true,
      },
    );

    dialogRef.closed
      .pipe(
        take(1),
        filter(
          (event: any) =>
            event?.reason === DialogCloseReason.Save ||
            event?.reason === DialogCloseReason.SaveAndClose,
        ),
        map((event: any) => event.data as string[]),
        tap((dashboardIds: string[]) => {
          this.selectedDashboardIds = dashboardIds.filter(
            (id) => id !== this.currentObject?.id,
          );
        }),
        switchMap((dashboardIds: string[]) => {
          if (dashboardIds.length === 0) {
            this.selectedDashboards = [];
            this.emitValidity();
            return EMPTY;
          }

          return this.userObjects.objectsByIds$(dashboardIds).pipe(
            take(1),
            tap((dashboards) => {
              this.selectedDashboards = dashboards;
              this.emitValidity();
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  removeDashboard(dashboardId: string): void {
    this.selectedDashboardIds = this.selectedDashboardIds.filter(
      (id) => id !== dashboardId,
    );
    this.selectedDashboards = this.selectedDashboards.filter(
      (dashboard) => dashboard.id !== dashboardId,
    );
    this.emitValidity();
  }

  getFormData(): OtherDashboardsOperation[] {
    if (!this.currentObject || this.selectedDashboardIds.length === 0)
      return [];

    return this.widgets.map((widget) => ({
      scenario: 'other-dashboards',
      widget_id: widget.id,
      source_object_id: this.currentObject!.id,
      source_object_class_id: this.currentObject!.object_class_id,
      target_object_ids: this.selectedDashboardIds as [string, ...string[]],
      target_object_class_id: NomObjectClass.DASHBOARD,
      action: this.globalAction,
      target_page: this.pageStrategy,
      switch_datasource:
        this.currentObject!.object_class_id !== NomObjectClass.DASHBOARD,
    }));
  }
}
